import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  User, Phone, Hash, Calendar, Stethoscope, Pill, FlaskConical, Heart, ShieldAlert, HeartPulse, Droplet, ArrowRight, Loader2, Activity,
} from 'lucide-react';
import DrawerShell from './DrawerShell';
import { Button } from '../components/ui/button';
import { formatDateIST } from '../utils/date';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * PatientQuickPeekDrawer — fast, read-only snapshot of a patient surfaced
 * from search results so the doctor doesn't have to leave context.
 * Shows: header chips · safety tags (allergies / chronic / blood group)
 * · latest vitals snapshot · last 3 visits · count badges. A primary
 * CTA opens the full /patients/:id page for deep work.
 */
export default function PatientQuickPeekDrawer({ open, onClose, patientId }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !patientId) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/api/patients/${patientId}/profile`, { withCredentials: true })
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, patientId]);

  const p = data?.patient;
  const sum = data?.summary;
  const visits = data?.visits || [];
  const latestVitals = visits.find(v => v.vitals && Object.keys(v.vitals).length > 0)?.vitals || {};

  return (
    <DrawerShell
      open={open}
      onClose={onClose}
      eyebrow="Quick Peek"
      title={loading ? 'Loading…' : (p?.name || 'Patient')}
      subtitle={p ? `MRN ${p.mrn}${p.age ? ` · ${p.age}y` : ''}${p.gender ? ' · ' + p.gender : ''}` : ''}
      accent="#7c3aed"
      icon={<User className="h-5 w-5" />}
      width="md"
      testId="patient-quick-peek"
      headerExtras={p && (
        <div className="flex flex-wrap gap-1.5 items-center">
          {p.phone && (
            <span className="inline-flex items-center gap-1 text-[11px] bg-white/15 ring-1 ring-white/30 px-2 py-0.5 rounded-full">
              <Phone className="h-3 w-3" />{p.phone}
            </span>
          )}
          {p.blood_group && (
            <span className="inline-flex items-center gap-1 text-[11px] bg-red-500/25 ring-1 ring-red-300/60 px-2 py-0.5 rounded-full font-bold">
              <Droplet className="h-3 w-3" />{p.blood_group}
            </span>
          )}
          {sum?.last_visit && (
            <span className="text-[11px] opacity-90">
              <Calendar className="h-3 w-3 inline mr-0.5" />Last visit {formatDateIST(sum.last_visit)}
            </span>
          )}
        </div>
      )}
      footer={p && (
        <>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button
            onClick={() => { onClose(); navigate(`/patients/${patientId}`); }}
            data-testid="quickpeek-open-full"
          >
            Open full profile <ArrowRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </>
      )}
    >
      {loading ? (
        <div className="p-10 text-center text-zinc-400"><Loader2 className="h-6 w-6 mx-auto animate-spin" /></div>
      ) : !p ? (
        <div className="p-10 text-center text-zinc-400">Patient not found.</div>
      ) : (
        <div className="p-5 space-y-4">
          {/* SAFETY TAGS */}
          {(p.allergies?.length > 0 || p.chronic_conditions?.length > 0) && (
            <div className="rounded-xl border border-red-200 bg-red-50/40 p-3 space-y-2" data-testid="quickpeek-safety">
              {p.allergies?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-red-700 mb-1 flex items-center gap-1"><ShieldAlert className="h-3 w-3" /> Allergies</div>
                  <div className="flex flex-wrap gap-1">
                    {p.allergies.map((a, i) => (
                      <span key={i} className="text-[11px] font-bold bg-red-100 text-red-800 border border-red-200 px-2 py-0.5 rounded-full">{a}</span>
                    ))}
                  </div>
                </div>
              )}
              {p.chronic_conditions?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700 mb-1 flex items-center gap-1"><HeartPulse className="h-3 w-3" /> Chronic conditions</div>
                  <div className="flex flex-wrap gap-1">
                    {p.chronic_conditions.map((c, i) => (
                      <span key={i} className="text-[11px] font-bold bg-amber-100 text-amber-800 border border-amber-200 px-2 py-0.5 rounded-full">{c}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* SUMMARY STATS */}
          <div className="grid grid-cols-3 gap-2" data-testid="quickpeek-stats">
            <StatTile label="Visits" value={sum?.visits ?? 0} color="#0f766e" />
            <StatTile label="Rx total" value={sum?.prescriptions_total ?? 0} color="#dc2626" />
            <StatTile label="Investigations" value={sum?.investigations_total ?? 0} color="#0891b2" />
          </div>

          {/* LATEST VITALS */}
          {Object.keys(latestVitals).length > 0 && (
            <div className="rounded-xl border bg-white p-3" data-testid="quickpeek-vitals">
              <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-2 flex items-center gap-1"><Heart className="h-3 w-3 text-rose-500" /> Most recent vitals</div>
              <div className="flex flex-wrap gap-1.5">
                <VChip label="BP" v={latestVitals.bp_systolic && latestVitals.bp_diastolic ? `${latestVitals.bp_systolic}/${latestVitals.bp_diastolic}` : null} alarm={(latestVitals.bp_systolic >= 140 || latestVitals.bp_diastolic >= 90)} />
                <VChip label="HR" v={latestVitals.pulse} alarm={latestVitals.pulse && (latestVitals.pulse > 100 || latestVitals.pulse < 50)} />
                <VChip label="SpO₂" v={latestVitals.spo2 ? `${latestVitals.spo2}%` : null} alarm={latestVitals.spo2 && latestVitals.spo2 < 95} />
                <VChip label="T" v={latestVitals.temperature ? `${latestVitals.temperature}°` : null} alarm={latestVitals.temperature > 38} />
                <VChip label="Wt" v={latestVitals.weight ? `${latestVitals.weight}kg` : null} />
                <VChip label="BMI" v={latestVitals.bmi} alarm={latestVitals.bmi && (latestVitals.bmi > 30 || latestVitals.bmi < 17)} />
                <VChip label="RBS" v={latestVitals.rbs} alarm={latestVitals.rbs && latestVitals.rbs > 200} />
              </div>
            </div>
          )}

          {/* RECENT VISITS */}
          <div className="rounded-xl border bg-white" data-testid="quickpeek-visits">
            <div className="px-3 py-2 border-b bg-zinc-50/60 text-[10px] uppercase tracking-wider font-bold text-zinc-500 flex items-center gap-1">
              <Stethoscope className="h-3 w-3" /> Recent visits
              <span className="ml-auto text-zinc-400">{visits.length}</span>
            </div>
            {visits.length === 0 ? (
              <div className="text-xs text-zinc-400 italic px-3 py-4 text-center">No visits yet.</div>
            ) : (
              <ul className="divide-y divide-zinc-100">
                {visits.slice(0, 4).map((v, i) => (
                  <li key={v.consultation_id || i} className="px-3 py-2" data-testid={`quickpeek-visit-${i}`}>
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-[11px] font-mono text-zinc-500">{v.date ? formatDateIST(v.date) : '—'}</span>
                      <span className="text-sm font-bold text-zinc-900 truncate">{v.doctor_name || 'Doctor'}</span>
                      {(v.final_diagnosis || v.provisional_diagnosis) && (
                        <span className="text-[11px] bg-violet-50 text-violet-700 border border-violet-200 px-1.5 py-0 rounded truncate max-w-[260px]">
                          {v.final_diagnosis || v.provisional_diagnosis}
                        </span>
                      )}
                    </div>
                    {v.chief_complaint && <div className="text-[11px] text-zinc-500 mt-0.5 truncate">{v.chief_complaint}</div>}
                    <div className="flex gap-2 text-[10px] text-zinc-500 mt-0.5">
                      {v.prescription?.length > 0 && <span className="inline-flex items-center gap-0.5"><Pill className="h-2.5 w-2.5" />{v.prescription.length} Rx</span>}
                      {v.investigations?.length > 0 && <span className="inline-flex items-center gap-0.5"><FlaskConical className="h-2.5 w-2.5" />{v.investigations.length} test{v.investigations.length === 1 ? '' : 's'}</span>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </DrawerShell>
  );
}

function StatTile({ label, value, color }) {
  return (
    <div className="rounded-lg border bg-white p-2 relative overflow-hidden">
      <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: color }} />
      <div className="ml-1.5">
        <div className="text-[9px] uppercase tracking-wider font-bold text-zinc-500">{label}</div>
        <div className="text-base font-extrabold text-zinc-900 leading-none mt-0.5" style={{ fontFamily: 'Manrope' }}>{value}</div>
      </div>
    </div>
  );
}

function VChip({ label, v, alarm }) {
  if (v == null || v === '') return null;
  return (
    <span className={`text-[11px] px-2 py-1 rounded font-mono ${alarm ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-zinc-100 text-zinc-700'}`}>
      <span className="text-zinc-500 font-sans font-bold mr-1">{label}</span>{v}
      {alarm && <Activity className="h-2.5 w-2.5 inline ml-0.5" />}
    </span>
  );
}
